import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium ' +
    'transition-[background,box-shadow,transform,color] duration-150 select-none ' +
    'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-grad text-primary-fg shadow-[0_10px_24px_-14px_rgba(45,212,191,0.8)] hover:brightness-110 active:translate-y-px',
        solid: 'bg-primary text-primary-fg hover:brightness-110 active:translate-y-px',
        secondary:
          'bg-surface-2 text-fg border border-border hover:bg-surface-3 hover:border-border-strong',
        outline:
          'border border-border bg-transparent text-fg hover:bg-card-hover hover:border-border-strong',
        ghost: 'bg-transparent text-muted hover:bg-card-hover hover:text-fg',
        danger: 'bg-danger text-danger-fg hover:brightness-110 active:translate-y-px',
        link: 'text-secondary underline-offset-4 hover:underline px-0',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-11 px-6 text-base',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as the child element (e.g. an <a>) while keeping button styles. */
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        type={asChild ? undefined : (type ?? 'button')}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'
